import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} VariantModalRefs
 * @property {HTMLDivElement} overlay - The overlay backdrop
 * @property {HTMLDivElement} panel - The drawer panel
 * @property {HTMLDivElement} optionsContainer - The options container
 * @property {HTMLButtonElement} addToCartButton - The add to cart button inside the modal
 * @property {HTMLSpanElement} addToCartText - The add to cart button text
 * @property {HTMLDivElement} [stockLegend] - The low stock legend
 * @property {HTMLDivElement} [notifyView] - The back-in-stock notify view
 * @property {HTMLSpanElement} [notifySizeValue] - The chosen size value label
 * @property {HTMLDivElement} [notifyPills] - The sold-out size pills container
 * @property {HTMLParagraphElement} [notifyDescription] - The notify description copy
 * @property {HTMLParagraphElement} [notifySuccess] - The notify success message
 * @property {HTMLDivElement} [notifyForm] - The notify email form wrapper
 * @property {HTMLInputElement} [notifyEmail] - The notify email input
 * @property {HTMLButtonElement} [notifySubmit] - The notify submit button
 * @property {HTMLInputElement} [notifyConsent] - The newsletter consent checkbox
 * @property {HTMLParagraphElement} [notifyError] - The notify error message
 */

const KLAVIYO_CLIENT_API = 'https://a.klaviyo.com/client';
const KLAVIYO_REVISION = '2026-07-15';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * A variant selection drawer/modal.
 * Desktop: slides in from the right (420px).
 * Mobile: slides up as a bottom sheet.
 *
 * Delegates variant selection to the page's existing variant-picker by
 * programmatically clicking the matching radio input, so the full
 * ThemeEvents flow (variantSelected → variantUpdate) fires naturally.
 *
 * Add to cart flow: sets `data-skip-next-submit` on the product form, then
 * calls `form.requestSubmit()`. Monster Upsell intercepts the submit event,
 * handles the cart XHR itself, and dispatches `cart:updated` on document.
 * We listen for that to close the modal. Our own `handleSubmit` exits early
 * when the skip flag is set, preventing a double add.
 *
 * @extends {Component<VariantModalRefs>}
 */
class VariantModal extends Component {
  requiredRefs = ['overlay', 'panel', 'addToCartButton', 'addToCartText'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {string | null} Variant id the notify subscription targets */
  #notifyVariantId = null;

  /** @type {boolean} */
  #isNotifySubmitting = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');

    section?.addEventListener(
      ThemeEvents.variantUpdate,
      /** @type {EventListener} */ (this.#handleVariantUpdate),
      { signal }
    );
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });
    // Monster Upsell dispatches `cart:updated` on document after its own /cart/add XHR.
    document.addEventListener('cart:updated', this.#handleThirdPartyCartUpdated, { signal });
    document.addEventListener('keyup', this.#handleKeyUp, { signal });

    if (this.dataset.currentVariantAvailable === 'false') {
      this.#notifyVariantId = this.dataset.currentVariantId ?? null;
    }
    this.refs.notifyEmail?.addEventListener('input', this.#handleNotifyEmailInput, { signal });
    this.refs.notifyEmail?.addEventListener('keydown', this.#handleNotifyEmailKeydown, { signal });

    this.#updateStockLegend();
    this.#syncButtonState();
    this.#bindTriggerButtons(signal);
    this.#revealSizeGuide();
  }

  /**
   * Reveals the size-guide prompt only when a `size-guide` content block lives
   * in the same section. The prompt's trigger carries `data-size-guide-trigger`,
   * which that block's own delegation (on `.shopify-section`) listens for — so we
   * just unhide it when the drawer is actually present. Templates without a
   * size-guide block keep the prompt hidden instead of showing a dead link.
   */
  #revealSizeGuide() {
    const sizeGuide = this.refs.sizeGuide;
    if (!sizeGuide) return;
    const hasSizeGuide = this.closest('.shopify-section')?.querySelector('[data-size-guide-modal]');
    if (hasSizeGuide) sizeGuide.hidden = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @returns {boolean} */
  get isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    if (this.isOpen) return;
    this.setAttribute('open', '');
    document.documentElement.setAttribute('scroll-lock', '');
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    document.documentElement.removeAttribute('scroll-lock');
    this.#resetNotifyView();
  }

  /** @returns {boolean} Whether back-in-stock notifications are configured */
  get #canNotify() {
    return Boolean(this.dataset.klaviyoKey && this.refs.notifyView);
  }

  /**
   * Handle option button click — delegate to page variant-picker radio input.
   * @param {MouseEvent} event
   */
  selectOption(event) {
    const btn = /** @type {HTMLButtonElement} */ (event.target);
    if (btn.getAttribute('aria-disabled') === 'true') return;

    const { optionValue, optionPosition, optionValueId } = btn.dataset;
    if (!optionValue || !optionPosition) return;

    // Update visual selection state within the fieldset
    btn.closest('.variant-modal__fieldset')
      ?.querySelectorAll('.variant-modal__option-btn')
      .forEach((b) => {
        b.classList.remove('variant-modal__option-btn--selected');
        b.setAttribute('aria-pressed', 'false');
      });
    btn.classList.add('variant-modal__option-btn--selected');
    btn.setAttribute('aria-pressed', 'true');

    // Trigger the page's variant-picker so the full ThemeEvents flow fires
    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${this.dataset.productId}"]`
    );
    if (!variantPicker) return;

    const matchingInput = /** @type {HTMLInputElement | null} */ (
      variantPicker.querySelector(`input[data-option-value-id="${optionValueId}"]`)
    );
    if (matchingInput && !matchingInput.checked) {
      matchingInput.click();
    }
  }

  /**
   * Handle add to cart click.
   * When the selected variant is sold out and Klaviyo is configured, the button
   * reads "Get notified" and opens the notify view instead of adding to cart.
   * Otherwise sets `data-skip-next-submit` so our `handleSubmit` exits early,
   * then calls `form.requestSubmit()` so Monster Upsell can intercept as normal.
   */
  handleAddToCart() {
    if (this.refs.addToCartButton.disabled || this.#isSubmitting) return;

    if (this.dataset.cta === 'notify') {
      this.#showNotifyView();
      return;
    }

    const section = this.closest('.shopify-section');
    if (!section) return;

    const productForm = /** @type {HTMLElement | null} */ (
      section.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`)
    );
    if (!productForm) return;

    const form = /** @type {HTMLFormElement | null} */ (productForm.querySelector('form'));
    if (!form) return;

    this.#isSubmitting = true;
    this.refs.addToCartButton.disabled = true;

    // Flag for product-form-component to skip its own handleSubmit.
    productForm.dataset.skipNextSubmit = 'true';
    form.requestSubmit();
  }

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const { addToCartButton, addToCartText } = this.refs;
    delete this.dataset.cta;

    if (variant == null) {
      this.#notifyVariantId = null;
      addToCartButton.disabled = true;
      addToCartText.textContent = Theme.translations.unavailable || 'Unavailable';
      return;
    }

    this.#syncOptionSelection(variant);

    if (variant.available) {
      this.#notifyVariantId = null;
      addToCartButton.disabled = this.#isSubmitting;
      addToCartText.textContent = Theme.translations.add_to_cart || 'Add to cart';
    } else {
      this.#notifyVariantId = String(variant.id);
      if (this.#canNotify) {
        this.dataset.cta = 'notify';
        addToCartButton.disabled = false;
        addToCartText.textContent = Theme.translations.notify_me || 'Notify me';
        this.#syncNotifyView();
      } else {
        addToCartButton.disabled = true;
        addToCartText.textContent = Theme.translations.sold_out || 'Sold out';
      }
    }
  };

  /**
   * Mirrors the variant's option values onto the modal's option buttons.
   * Needed when the variant changes without a direct button click (e.g. a
   * notify-view pill delegated to the page variant-picker) — the modal buttons
   * are not part of the section morph, so their selected state goes stale.
   * @param {any} variant
   */
  #syncOptionSelection(variant) {
    const values = variant?.options;
    if (!Array.isArray(values)) return;

    this.querySelectorAll('.variant-modal__fieldset').forEach((fieldset) => {
      const position = Number(/** @type {HTMLElement} */ (fieldset).dataset.optionPosition);
      const value = values[position - 1];
      if (value == null) return;

      fieldset.querySelectorAll('.variant-modal__option-btn').forEach((btn) => {
        const selected = /** @type {HTMLElement} */ (btn).dataset.optionValue === String(value);
        btn.classList.toggle('variant-modal__option-btn--selected', selected);
        btn.setAttribute('aria-pressed', String(selected));
      });
    });
  }

  /**
   * Handles cart:update (theme's own cart event — fired when our fetch succeeds).
   * In practice, Monster Upsell prevents our fetch from ever running on submit,
   * so this fires when the sticky ATC direct-clicks the main button instead.
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    if (!this.#isSubmitting) return;

    const didError = /** @type {any} */ (event).detail?.data?.didError;

    this.#isSubmitting = false;
    this.refs.addToCartButton.disabled = false;

    if (didError) {
      this.#syncButtonState();
      return;
    }

    if (this.isOpen) this.close();
    this.#syncButtonState();
  };

  /**
   * Handles `cart:updated` from Monster Upsell (third-party).
   * Monster Upsell intercepts form submit, does its own /cart/add XHR, opens
   * its drawer, then dispatches this event. We use it to close the modal.
   */
  #handleThirdPartyCartUpdated = () => {
    if (!this.#isSubmitting) return;

    this.#isSubmitting = false;
    this.refs.addToCartButton.disabled = false;

    if (this.isOpen) this.close();
    this.#syncButtonState();
  };

  /**
   * Cart error — re-enable the button so the user can retry.
   */
  #handleCartError = () => {
    if (!this.#isSubmitting) return;

    this.#isSubmitting = false;
    this.#syncButtonState();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  /**
   * Show/hide stock legend based on whether any low-stock dots exist.
   */
  #updateStockLegend() {
    const { stockLegend, optionsContainer } = this.refs;
    if (!stockLegend || !optionsContainer) return;
    stockLegend.style.display = optionsContainer.querySelector('.variant-modal__low-stock-dot')
      ? 'flex'
      : 'none';
  }

  /**
   * Syncs the ATC button state from the currently selected option button.
   * Called on init, after cart events, and after errors so button text/state
   * always matches the selected variant.
   */
  #syncButtonState() {
    const { addToCartButton, addToCartText } = this.refs;

    const selectedBtn = /** @type {HTMLElement | null} */ (
      this.querySelector('.variant-modal__option-btn--selected')
    );

    if (!selectedBtn) {
      // No option selected yet — keep button disabled, show default text
      addToCartButton.disabled = true;
      addToCartText.textContent = Theme.translations.add_to_cart || 'Add to cart';
      return;
    }

    const isAvailable = selectedBtn.dataset.optionAvailable === 'true';
    delete this.dataset.cta;

    if (isAvailable) {
      addToCartButton.disabled = this.#isSubmitting;
      addToCartText.textContent = Theme.translations.add_to_cart || 'Add to cart';
    } else if (this.#canNotify) {
      this.dataset.cta = 'notify';
      addToCartButton.disabled = false;
      addToCartText.textContent = Theme.translations.notify_me || 'Notify me';
    } else {
      addToCartButton.disabled = true;
      addToCartText.textContent = Theme.translations.sold_out || 'Sold out';
    }
  }

  /**
   * Switches the panel to the notify (back in stock) view.
   */
  #showNotifyView() {
    if (!this.#canNotify) return;

    // FLIP the panel height so the mobile bottom sheet resizes smoothly while
    // the notify view fades in (desktop drawer is full-height — no-op there).
    const { panel } = this.refs;
    const startHeight = panel.offsetHeight;

    this.dataset.view = 'notify';
    this.#syncNotifyView();

    const endHeight = panel.offsetHeight;
    if (startHeight !== endHeight) {
      panel.style.height = `${startHeight}px`;
      panel.offsetHeight; // force reflow so the transition starts from here
      panel.style.height = `${endHeight}px`;
      setTimeout(() => {
        panel.style.height = '';
        if (this.isOpen) this.refs.notifyEmail?.focus({ preventScroll: true });
      }, 320);
    } else {
      this.refs.notifyEmail?.focus({ preventScroll: true });
    }
  }

  /**
   * Resets the panel back to the choose-size view (unless the modal is
   * notify-only) and clears any submitted/error state for the next open.
   */
  #resetNotifyView() {
    if (!this.refs.notifyView) return;

    if (!('notifyOnly' in this.dataset)) {
      delete this.dataset.view;
    }

    const { notifyForm, notifySuccess, notifyDescription, notifyError, notifyPills } = this.refs;
    if (notifyForm) notifyForm.hidden = false;
    if (notifySuccess) notifySuccess.hidden = true;
    if (notifyDescription) notifyDescription.hidden = false;
    if (notifyError) notifyError.hidden = true;
    if (notifyPills) notifyPills.hidden = false;
  }

  /**
   * Syncs the chosen-size label and sold-out pill selection with the
   * currently selected (sold out) variant.
   */
  #syncNotifyView() {
    const { notifySizeValue, notifyPills } = this.refs;

    /** @type {HTMLElement | null} */
    let selectedPill = null;
    if (notifyPills && this.#notifyVariantId) {
      notifyPills.querySelectorAll('.variant-modal__notify-pill').forEach((pill) => {
        const isSelected =
          /** @type {HTMLElement} */ (pill).dataset.variantId === this.#notifyVariantId;
        pill.classList.toggle('variant-modal__notify-pill--selected', isSelected);
        if (isSelected) selectedPill = /** @type {HTMLElement} */ (pill);
      });
    }

    if (notifySizeValue) {
      const selected = /** @type {HTMLElement[]} */ ([
        ...this.querySelectorAll('.variant-modal__option-btn--selected'),
      ]);
      notifySizeValue.textContent =
        selectedPill?.dataset.optionValue ||
        selected.map((btn) => btn.dataset.optionValue).join(' / ');
    }
  }

  /**
   * Handle a sold-out size pill click inside the notify view. Delegates to the
   * page variant-picker radio (like `selectOption`) so the variantUpdate event
   * keeps every piece of UI in sync; falls back to local state when no radio
   * matches.
   * @param {MouseEvent} event
   */
  selectNotifySize(event) {
    const pill = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('.variant-modal__notify-pill')
    );
    if (!pill) return;

    const { optionValueId, variantId } = pill.dataset;

    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${this.dataset.productId}"]`
    );
    const matchingInput = /** @type {HTMLInputElement | null} */ (
      variantPicker?.querySelector(`input[data-option-value-id="${optionValueId}"]`) ?? null
    );

    if (matchingInput && !matchingInput.checked) {
      matchingInput.click();
      return;
    }

    if (variantId) {
      this.#notifyVariantId = variantId;
      this.#syncNotifyView();
    }
  }

  /**
   * Enables the submit button once the email looks valid.
   */
  #handleNotifyEmailInput = () => {
    const { notifyEmail, notifySubmit } = this.refs;
    if (!notifyEmail || !notifySubmit) return;
    notifySubmit.disabled =
      this.#isNotifySubmitting || !EMAIL_PATTERN.test(notifyEmail.value.trim());
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleNotifyEmailKeydown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (!this.refs.notifySubmit?.disabled) this.submitNotify();
  };

  /**
   * Creates the Klaviyo back-in-stock subscription for the chosen sold-out
   * variant and, when the shopper opted in, also subscribes them to the
   * newsletter list. Klaviyo's client API is CORS-enabled and idempotent per
   * profile+variant, and returns 202 on success.
   */
  async submitNotify() {
    const { notifyEmail, notifySubmit, notifyForm, notifySuccess, notifyDescription, notifyError } =
      this.refs;
    const companyId = this.dataset.klaviyoKey;
    const email = notifyEmail?.value.trim();

    if (!companyId || !email || !this.#notifyVariantId || this.#isNotifySubmitting) return;

    this.#isNotifySubmitting = true;
    if (notifySubmit) notifySubmit.disabled = true;
    if (notifyError) notifyError.hidden = true;

    const headers = {
      accept: 'application/vnd.api+json',
      'content-type': 'application/vnd.api+json',
      revision: KLAVIYO_REVISION,
    };

    try {
      const response = await fetch(
        `${KLAVIYO_CLIENT_API}/back-in-stock-subscriptions?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: { data: { type: 'profile', attributes: { email } } },
                channels: ['EMAIL'],
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${this.#notifyVariantId}`,
                  },
                },
              },
            },
          }),
        }
      );

      if (!response.ok && response.status !== 202) throw new Error(`Klaviyo BIS ${response.status}`);

      // Newsletter opt-in: Klaviyo's Create Client Subscription requires a
      // list relationship, so this only runs when a list id is configured.
      if (this.refs.notifyConsent?.checked && this.dataset.klaviyoList) {
        // Fire and forget — the BIS subscription is the primary action.
        fetch(`${KLAVIYO_CLIENT_API}/subscriptions?company_id=${encodeURIComponent(companyId)}`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Back in stock modal',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                    },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: this.dataset.klaviyoList } },
              },
            },
          }),
        }).catch(() => {});
      }

      if (notifyForm) notifyForm.hidden = true;
      if (notifyDescription) notifyDescription.hidden = true;
      if (this.refs.notifyPills) this.refs.notifyPills.hidden = true;
      if (notifySuccess) notifySuccess.hidden = false;
    } catch (error) {
      if (notifyError) notifyError.hidden = false;
    } finally {
      this.#isNotifySubmitting = false;
      this.#handleNotifyEmailInput();
    }
  }

  /**
   * Binds click listeners to all [data-variant-modal-trigger] buttons in section.
   * @param {AbortSignal} signal
   */
  #bindTriggerButtons(signal) {
    const section = this.closest('.shopify-section');
    if (!section) return;

    section.addEventListener(
      'click',
      (event) => {
        if (/** @type {HTMLElement} */ (event.target).closest('[data-variant-modal-trigger]')) {
          event.preventDefault();
          this.open();
        }
      },
      { signal }
    );
  }
}

if (!customElements.get('variant-modal')) {
  customElements.define('variant-modal', VariantModal);
}
